<template>
  <div class="py-3 px-6 new-back fill-height">
		<div class="d-flex flex-column gap-4">
			<div class="d-flex align-center justify-space-between">
				<div class="flex-grow-1">
					<p class="text-body-1">
						API tokens allow external applications to authenticate and access the BlueSurf API. 
						Create tokens with specific permissions to control what actions can be performed.
					</p>
					<p class="text-caption grey--text">
						<v-icon small class="mr-1" style="margin-top: -.4rem;">mdi-book-open-variant</v-icon>
						For detailed API usage instructions, refer to the 
						<router-link to="/api-documentation" class="text-decoration-none">API documentation</router-link>.
					</p>
				</div>
				<v-btn v-if="$security.can('add')" color="primary" @click="openCreateDialog()">
					<v-icon left>add</v-icon>
					Create Token
				</v-btn>
			</div>
	
			<v-card class="mb-4 active-tokens-card" elevation="2">
				<v-card-title class="active-tokens-header py-2 clickable" @click="expanded.active = !expanded.active">
					<v-icon left color="success">mdi-check-circle</v-icon>
					<span>Active Tokens</span>
					<v-chip small color="success" text-color="white" class="ml-2">
						{{ activeTokens.length }}
					</v-chip>
					<v-spacer></v-spacer>
					<v-icon>{{ expanded.active ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
				</v-card-title>
				<v-card-text v-show="expanded.active" class="pa-0">
					<api-token-table @revoke="loadTokens" @edit="openUpdateDialog" :items="activeTokens" :permissions="permissions"/>
				</v-card-text>
			</v-card>
	
			<v-card class="mb-4 revoked-tokens-card" elevation="2">
				<v-card-title class="revoked-tokens-header py-2 clickable" @click="expanded.revoked = !expanded.revoked">
					<v-icon left color="error">mdi-cancel</v-icon>
					<span>Revoked Tokens</span>
					<v-chip small color="error" text-color="white" class="ml-2">
						{{ revokedTokens.length }}
					</v-chip>
					<v-spacer></v-spacer>
					<v-icon>{{ expanded.revoked ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
				</v-card-title>
				<v-card-text v-show="expanded.revoked" class="pa-0">
					<api-token-table :items="revokedTokens" :permissions="permissions"/>
				</v-card-text>
			</v-card>
	
			<v-card class="expired-tokens-card" elevation="2">
				<v-card-title class="expired-tokens-header py-2 clickable" @click="expanded.expired = !expanded.expired">
					<v-icon left color="orange">mdi-alert-circle</v-icon>
					<span>Expired Tokens</span>
					<v-chip small color="orange" text-color="white" class="ml-2">
						{{ expiredTokens.length }}
					</v-chip>
					<v-spacer></v-spacer>
					<v-icon>{{ expanded.expired ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
				</v-card-title>
				<v-card-text v-show="expanded.expired" class="pa-0">
					<api-token-table :items="expiredTokens" :permissions="permissions"/>
				</v-card-text>
			</v-card>
		</div>
		<api-token-create-dialog ref="createDialog" @save="loadTokens" :permissions="permissions"/>
		<api-token-update-dialog ref="updateDialog" @save="loadTokens" :permissions="permissions"/>
  </div>
</template>

<script>
import _ from "lodash"
import apiTokenPermissionsJSON from "../../json/api-token-permissions.json"
import AccessLevelSelect from '../../components/AccessLevelSelect'
import ApiTokenTable from '../../components/api-token/ApiTokenTable'
import ApiTokenCreateDialog from '../../components/api-token/ApiTokenCreateDialog'
import ApiTokenUpdateDialog from '../../components/api-token/ApiTokenUpdateDialog'

export default {
  title: 'API Tokens',
  components: {
    AccessLevelSelect,
    ApiTokenTable,
    ApiTokenCreateDialog,
    ApiTokenUpdateDialog
  },
  data: () => ({
    loading: false,
    apiTokens: [],
    createDialog: false,
    valid: false,
    permissions: [],
    expanded: {
      active: true,
      revoked: true,
      expired: true
    }
  }),
  computed: {
    activeTokens() {
      return this.apiTokens.filter(token => token.status === 0);
    },
    revokedTokens() {
      return this.apiTokens.filter(token => token.status === 1);
    },
    expiredTokens() {
      return this.apiTokens.filter(token => token.status === 2);
    }
  },
  created() {
    this.init();
  },
  methods: {
    init() {
      this.loadPermissions();
      this.loadTokens();
    },
    loadPermissions() {
      this.permissions = apiTokenPermissionsJSON.permissions.map(p => ({
        ...p,
        id: p.value || p.name
      }));
    },
    loadTokens() {
      this.loading = true;
      this.$http.get("/api/apiToken")
        .then(res => {
          this.apiTokens = res.data;
          this.loading = false;
        })
        .catch(res => {
          window.getApp.error("Cannot obtain API tokens.");
          this.loading = false;
          console.log(res);
        });
    },
    openCreateDialog() {
      this.$refs.createDialog.openCreateDialog();
    },
    openUpdateDialog(token) {
      this.$refs.updateDialog.openUpdateDialog(token);
    },
  }
}
</script>

<style scoped lang="scss">
.active-tokens-card {
  border-left: 4px solid var(--v-success-base);
  
  .active-tokens-header {
    background-color: rgba(76, 175, 80, 0.1);
    color: var(--v-success-base);
    font-weight: 600;
    
    &.clickable {
      cursor: pointer;
      user-select: none;
      
      &:hover {
        background-color: rgba(76, 175, 80, 0.15);
      }
    }
  }
}

.revoked-tokens-card {
  border-left: 4px solid var(--v-error-base);
  opacity: 0.85;
  
  .revoked-tokens-header {
    background-color: rgba(244, 67, 54, 0.1);
    color: var(--v-error-base);
    font-weight: 600;
    
    &.clickable {
      cursor: pointer;
      user-select: none;
      
      &:hover {
        background-color: rgba(244, 67, 54, 0.15);
      }
    }
  }
}

.expired-tokens-card {
  border-left: 4px solid var(--v-warning-darken);
  opacity: 0.85;
  
  .expired-tokens-header {
    background-color: rgba(255, 152, 0, 0.1);
    color: var(--v-warning-darken);
    font-weight: 600;
    
    &.clickable {
      cursor: pointer;
      user-select: none;
      
      &:hover {
        background-color: rgba(255, 152, 0, 0.15);
      }
    }
  }
  
  .expired-table {
    ::v-deep tbody tr {
      opacity: 0.7;
    }
    
    .expired-code {
      opacity: 0.6;
      text-decoration: line-through;
    }
  }
}
</style>
